<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  templates: {
    type: Array,
    default: () => [],
  },
  analytics: {
    type: Object,
    default: () => ({}),
  },
  wabaId: {
    type: String,
    default: '',
  },
  isSyncing: {
    type: Boolean,
    default: false,
  },
  selectedDateRange: {
    type: String,
    default: 'LAST_7_DAYS',
  },
});

const emit = defineEmits([
  'create',
  'sync',
  'view',
  'edit',
  'delete',
  'switchView',
  'updateDateRange',
]);

const { t } = useI18n();

// Table Filter State
const searchQuery = ref('');
const selectedCategory = ref('ALL');
const selectedLanguage = ref('ALL');
const selectedStatus = ref('ALL');
const sortKey = ref('last_updated_time');
const sortOrder = ref('desc'); // 'asc' | 'desc'
const selectedTemplateIds = ref([]);

// Options
const CATEGORIES = ['ALL', 'UTILITY', 'MARKETING', 'AUTHENTICATION'];
const STATUSES = ['ALL', 'APPROVED', 'PENDING', 'REJECTED', 'PAUSED', 'DISABLED'];
const DATE_RANGES = [
  { key: 'LAST_7_DAYS', label: 'INBOX_MGMT.WHATSAPP_TEMPLATES.DATE_RANGE.LAST_7_DAYS' },
  { key: 'LAST_14_DAYS', label: 'INBOX_MGMT.WHATSAPP_TEMPLATES.DATE_RANGE.LAST_14_DAYS' },
  { key: 'LAST_30_DAYS', label: 'INBOX_MGMT.WHATSAPP_TEMPLATES.DATE_RANGE.LAST_30_DAYS' },
  { key: 'LAST_90_DAYS', label: 'INBOX_MGMT.WHATSAPP_TEMPLATES.DATE_RANGE.LAST_90_DAYS' },
];

const availableLanguages = computed(() => {
  const langs = new Set();
  props.templates.forEach(tpl => {
    if (tpl.language) langs.add(tpl.language);
  });
  return ['ALL', ...Array.from(langs)];
});

const getBodySnippet = template => {
  const bodyComponent = template.components?.find(c => c.type === 'BODY');
  return bodyComponent?.text || '';
};

const getTemplateAnalytics = template => {
  const tplId = (template.id || '').toString();
  return (
    props.analytics[tplId] || {
      sent: 0,
      delivered: 0,
      read: 0,
      clicked: 0,
      spent: null,
      cost_per_delivered: null,
    }
  );
};

const getStatusBadge = template => {
  const status = template.status?.toUpperCase() || 'PENDING';
  const quality = template.quality_score?.score?.toUpperCase();

  if (status === 'APPROVED') {
    let qualityLabel = t('INBOX_MGMT.WHATSAPP_TEMPLATES.QUALITY.ACTIVE_PENDING');
    if (quality === 'GREEN') qualityLabel = t('INBOX_MGMT.WHATSAPP_TEMPLATES.QUALITY.ACTIVE_HIGH');
    else if (quality === 'YELLOW') qualityLabel = t('INBOX_MGMT.WHATSAPP_TEMPLATES.QUALITY.ACTIVE_MEDIUM');
    else if (quality === 'RED') qualityLabel = t('INBOX_MGMT.WHATSAPP_TEMPLATES.QUALITY.ACTIVE_LOW');

    return {
      label: qualityLabel,
      bg: 'bg-n-teal-3',
      text: 'text-n-teal-11',
      dot: 'bg-n-teal-9',
      icon: 'i-lucide-circle-check',
    };
  }

  if (status === 'PENDING') {
    return {
      label: t('INBOX_MGMT.WHATSAPP_TEMPLATES.STATUS.PENDING'),
      bg: 'bg-n-amber-3',
      text: 'text-n-amber-11',
      dot: 'bg-n-amber-9',
      icon: 'i-lucide-clock',
    };
  }

  if (status === 'REJECTED') {
    return {
      label: t('INBOX_MGMT.WHATSAPP_TEMPLATES.STATUS.REJECTED'),
      bg: 'bg-n-ruby-3',
      text: 'text-n-ruby-10',
      dot: 'bg-n-ruby-9',
      icon: 'i-lucide-circle-x',
    };
  }

  return {
    label: t(`INBOX_MGMT.WHATSAPP_TEMPLATES.STATUS.${status}`) || status,
    bg: 'bg-n-alpha-2',
    text: 'text-n-slate-11',
    dot: 'bg-n-slate-9',
    icon: 'i-lucide-info',
  };
};

const formatDate = dateValue => {
  if (!dateValue) return '--';
  const date = typeof dateValue === 'number' ? new Date(dateValue * 1000) : new Date(dateValue);
  if (isNaN(date.getTime())) return '--';

  return new Intl.DateTimeFormat('es-ES', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

const formatLanguage = code => {
  if (!code) return '--';
  const lower = code.toLowerCase();
  if (lower.startsWith('es')) return 'Spanish';
  if (lower.startsWith('en')) return code.includes('US') ? 'English (US)' : 'English';
  if (lower.startsWith('pt')) return 'Portuguese';
  return code;
};

// Filtered & Sorted Templates
const filteredTemplates = computed(() => {
  return props.templates.filter(tpl => {
    // Search query
    if (searchQuery.value.trim()) {
      const q = searchQuery.value.toLowerCase();
      const nameMatch = tpl.name?.toLowerCase().includes(q);
      const bodyMatch = getBodySnippet(tpl).toLowerCase().includes(q);
      if (!nameMatch && !bodyMatch) return false;
    }

    // Category
    if (selectedCategory.value !== 'ALL') {
      if (tpl.category?.toUpperCase() !== selectedCategory.value) return false;
    }

    // Language
    if (selectedLanguage.value !== 'ALL') {
      if (tpl.language !== selectedLanguage.value) return false;
    }

    // Status
    if (selectedStatus.value !== 'ALL') {
      if (tpl.status?.toUpperCase() !== selectedStatus.value) return false;
    }

    return true;
  });
});

const sortedTemplates = computed(() => {
  const list = [...filteredTemplates.value];
  list.sort((a, b) => {
    let valA = a[sortKey.value];
    let valB = b[sortKey.value];

    if (sortKey.value === 'sent') {
      valA = getTemplateAnalytics(a).sent || 0;
      valB = getTemplateAnalytics(b).sent || 0;
    } else if (sortKey.value === 'read') {
      valA = getTemplateAnalytics(a).read || 0;
      valB = getTemplateAnalytics(b).read || 0;
    }

    if (valA === undefined || valA === null) valA = '';
    if (valB === undefined || valB === null) valB = '';

    if (typeof valA === 'string') {
      return sortOrder.value === 'asc'
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    }

    return sortOrder.value === 'asc' ? valA - valB : valB - valA;
  });
  return list;
});

const handleSort = key => {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortKey.value = key;
    sortOrder.value = 'desc';
  }
};

const isAllSelected = computed(() => {
  return (
    sortedTemplates.value.length > 0 &&
    selectedTemplateIds.value.length === sortedTemplates.value.length
  );
});

const toggleSelectAll = () => {
  if (isAllSelected.value) {
    selectedTemplateIds.value = [];
  } else {
    selectedTemplateIds.value = sortedTemplates.value.map(t => t.id || t.name);
  }
};

const toggleSelect = id => {
  const index = selectedTemplateIds.value.indexOf(id);
  if (index > -1) {
    selectedTemplateIds.value.splice(index, 1);
  } else {
    selectedTemplateIds.value.push(id);
  }
};

const canEdit = template => {
  const status = template.status?.toUpperCase();
  return ['APPROVED', 'REJECTED', 'PAUSED'].includes(status);
};

const metaTemplateUrl = template => {
  return `https://business.facebook.com/wa/manage/message-templates/?waba_id=${props.wabaId}&templates_tab=category_updates`;
};

const activeTemplatesCount = computed(() => {
  return props.templates.filter(t => t.status?.toUpperCase() === 'APPROVED').length;
});
</script>

<template>
  <div class="flex flex-col gap-4 w-full">
    <!-- Top Filter Bar -->
    <div
      class="flex flex-wrap items-center justify-between gap-3 p-3 rounded-2xl bg-n-solid-2 border border-n-weak shadow-sm"
    >
      <!-- Filters Left Group -->
      <div class="flex flex-wrap items-center gap-2 flex-1 min-w-[280px]">
        <!-- Search -->
        <div class="relative flex items-center min-w-[180px] max-w-xs flex-1 h-8">
          <span
            class="i-lucide-search absolute left-3 top-1/2 -translate-y-1/2 size-4 text-n-slate-10 pointer-events-none z-10 block"
          />
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.TABLE.SEARCH_PLACEHOLDER')"
            class="w-full !h-8 !m-0 !py-0 !pl-9 pr-3 text-xs bg-n-alpha-1 hover:bg-n-alpha-2 focus:bg-n-solid-1 text-n-slate-12 placeholder-n-slate-10 rounded-xl border border-n-weak focus:outline-none focus:ring-1 focus:ring-n-brand !leading-8 transition-all"
          />
        </div>

        <!-- Category Dropdown -->
        <div class="relative flex items-center h-8">
          <select
            v-model="selectedCategory"
            class="!h-8 !m-0 !py-0 appearance-none pl-3 pr-7 text-xs bg-n-alpha-1 hover:bg-n-alpha-2 text-n-slate-12 rounded-xl border border-n-weak focus:outline-none focus:ring-1 focus:ring-n-brand cursor-pointer font-medium !leading-8"
          >
            <option value="ALL">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.TABLE.ALL_CATEGORIES') }}
            </option>
            <option value="UTILITY">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.CATEGORY.UTILITY') }}
            </option>
            <option value="MARKETING">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.CATEGORY.MARKETING') }}
            </option>
            <option value="AUTHENTICATION">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.CATEGORY.AUTHENTICATION') }}
            </option>
          </select>
          <span
            class="i-lucide-chevron-down absolute right-2.5 top-1/2 -translate-y-1/2 size-3.5 text-n-slate-10 pointer-events-none block"
          />
        </div>

        <!-- Language Dropdown -->
        <div class="relative flex items-center h-8">
          <select
            v-model="selectedLanguage"
            class="!h-8 !m-0 !py-0 appearance-none pl-3 pr-7 text-xs bg-n-alpha-1 hover:bg-n-alpha-2 text-n-slate-12 rounded-xl border border-n-weak focus:outline-none focus:ring-1 focus:ring-n-brand cursor-pointer font-medium !leading-8"
          >
            <option value="ALL">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.TABLE.ALL_LANGUAGES') }}
            </option>
            <option
              v-for="lang in availableLanguages.slice(1)"
              :key="lang"
              :value="lang"
            >
              {{ formatLanguage(lang) }} ({{ lang }})
            </option>
          </select>
          <span
            class="i-lucide-chevron-down absolute right-2.5 top-1/2 -translate-y-1/2 size-3.5 text-n-slate-10 pointer-events-none block"
          />
        </div>

        <!-- Status / Options Dropdown -->
        <div class="relative flex items-center h-8">
          <select
            v-model="selectedStatus"
            class="!h-8 !m-0 !py-0 appearance-none pl-3 pr-7 text-xs bg-n-alpha-1 hover:bg-n-alpha-2 text-n-slate-12 rounded-xl border border-n-weak focus:outline-none focus:ring-1 focus:ring-n-brand cursor-pointer font-medium !leading-8"
          >
            <option value="ALL">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.TABLE.ALL_STATUSES') }}
            </option>
            <option value="APPROVED">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.STATUS.APPROVED') }}
            </option>
            <option value="PENDING">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.STATUS.PENDING') }}
            </option>
            <option value="REJECTED">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.STATUS.REJECTED') }}
            </option>
            <option value="PAUSED">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.STATUS.PAUSED') }}
            </option>
          </select>
          <span
            class="i-lucide-chevron-down absolute right-2.5 top-1/2 -translate-y-1/2 size-3.5 text-n-slate-10 pointer-events-none block"
          />
        </div>

        <!-- Date Range Filter -->
        <div class="relative flex items-center h-8">
          <span
            class="i-lucide-calendar absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-n-slate-10 pointer-events-none z-10 block"
          />
          <select
            :value="selectedDateRange"
            class="!h-8 !m-0 !py-0 !pl-8 pr-7 [background-image:none] !bg-none appearance-none text-xs bg-n-alpha-1 hover:bg-n-alpha-2 text-n-slate-12 rounded-xl border border-n-weak focus:outline-none focus:ring-1 focus:ring-n-brand cursor-pointer font-medium !leading-8"
            @change="$emit('updateDateRange', $event.target.value)"
          >
            <option
              v-for="range in DATE_RANGES"
              :key="range.key"
              :value="range.key"
            >
              {{ $t(range.label) }}
            </option>
          </select>
          <span
            class="i-lucide-chevron-down absolute right-2.5 top-1/2 -translate-y-1/2 size-3.5 text-n-slate-10 pointer-events-none block"
          />
        </div>
      </div>

      <!-- Actions Right Group -->
      <div class="flex items-center gap-2">
        <!-- View Toggle (Grid / Table) -->
        <div
          class="flex items-center h-8 p-0.5 rounded-xl bg-n-alpha-1 border border-n-weak"
        >
          <button
            type="button"
            v-tooltip="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.VIEW_MODE.GRID')"
            class="h-7 px-2 rounded-lg text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors flex items-center justify-center"
            @click="$emit('switchView', 'grid')"
          >
            <span class="i-lucide-layout-grid text-sm block" />
          </button>
          <button
            type="button"
            v-tooltip="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.VIEW_MODE.TABLE')"
            class="h-7 px-2 rounded-lg bg-n-solid-1 text-n-brand shadow-sm font-bold transition-all flex items-center justify-center"
            @click="$emit('switchView', 'table')"
          >
            <span class="i-lucide-table text-sm block" />
          </button>
        </div>

        <!-- Sync Button -->
        <NextButton
          sm
          ghost
          slate
          :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.SYNC_BUTTON')"
          icon="i-lucide-refresh-cw"
          :is-loading="isSyncing"
          @click="$emit('sync')"
        />

        <!-- Create Button -->
        <NextButton
          sm
          :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE_BUTTON')"
          icon="i-lucide-plus"
          @click="$emit('create')"
        />
      </div>
    </div>

    <!-- Table Card Container -->
    <div
      class="w-full rounded-2xl border border-n-weak bg-white dark:bg-n-solid-2 shadow-sm overflow-hidden flex flex-col"
    >
      <div class="overflow-x-auto">
        <table class="w-full text-left text-xs border-collapse min-w-[1050px]">
          <thead>
            <tr
              class="border-b border-n-weak bg-n-alpha-1 text-n-slate-11 select-none font-semibold"
            >
              <!-- Checkbox Column -->
              <th class="w-10 px-3 py-3 text-center">
                <input
                  type="checkbox"
                  :checked="isAllSelected"
                  class="rounded border-n-slate-7 text-n-brand focus:ring-n-brand cursor-pointer"
                  @change="toggleSelectAll"
                />
              </th>

              <!-- Nombre de la plantilla -->
              <th
                class="px-3 py-3 cursor-pointer hover:text-n-slate-12 transition-colors"
                @click="handleSort('name')"
              >
                <div class="flex items-center gap-1.5">
                  <span>{{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.TABLE.NAME') }}</span>
                  <span
                    class="i-lucide-arrow-up-down text-[11px] opacity-60"
                    :class="{ 'opacity-100 text-n-brand': sortKey === 'name' }"
                  />
                </div>
              </th>

              <!-- Categoría -->
              <th
                class="px-3 py-3 cursor-pointer hover:text-n-slate-12 transition-colors"
                @click="handleSort('category')"
              >
                <div class="flex items-center gap-1.5">
                  <span>{{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.TABLE.CATEGORY') }}</span>
                  <span
                    class="i-lucide-arrow-up-down text-[11px] opacity-60"
                    :class="{ 'opacity-100 text-n-brand': sortKey === 'category' }"
                  />
                </div>
              </th>

              <!-- Idioma -->
              <th
                class="px-3 py-3 cursor-pointer hover:text-n-slate-12 transition-colors"
                @click="handleSort('language')"
              >
                <div class="flex items-center gap-1.5">
                  <span>{{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.TABLE.LANGUAGE') }}</span>
                  <span
                    class="i-lucide-arrow-up-down text-[11px] opacity-60"
                    :class="{ 'opacity-100 text-n-brand': sortKey === 'language' }"
                  />
                </div>
              </th>

              <!-- Estado -->
              <th
                class="px-3 py-3 cursor-pointer hover:text-n-slate-12 transition-colors"
                @click="handleSort('status')"
              >
                <div class="flex items-center gap-1.5">
                  <span>{{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.TABLE.STATUS') }}</span>
                  <span
                    class="i-lucide-arrow-up-down text-[11px] opacity-60"
                    :class="{ 'opacity-100 text-n-brand': sortKey === 'status' }"
                  />
                </div>
              </th>

              <!-- Mensajes enviados -->
              <th
                class="px-3 py-3 cursor-pointer hover:text-n-slate-12 transition-colors"
                @click="handleSort('sent')"
              >
                <div class="flex items-center gap-1">
                  <span>{{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.TABLE.MESSAGES_SENT') }}</span>
                  <span
                    v-tooltip="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.TABLE.MESSAGES_SENT_TOOLTIP')"
                    class="i-lucide-info text-[11px] text-n-slate-10 hover:text-n-slate-12 cursor-help"
                  />
                  <span
                    class="i-lucide-arrow-up-down text-[11px] opacity-60"
                    :class="{ 'opacity-100 text-n-brand': sortKey === 'sent' }"
                  />
                </div>
              </th>

              <!-- Mensajes abiertos -->
              <th
                class="px-3 py-3 cursor-pointer hover:text-n-slate-12 transition-colors"
                @click="handleSort('read')"
              >
                <div class="flex items-center gap-1">
                  <span>{{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.TABLE.MESSAGES_OPENED') }}</span>
                  <span
                    v-tooltip="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.TABLE.MESSAGES_OPENED_TOOLTIP')"
                    class="i-lucide-info text-[11px] text-n-slate-10 hover:text-n-slate-12 cursor-help"
                  />
                  <span
                    class="i-lucide-arrow-up-down text-[11px] opacity-60"
                    :class="{ 'opacity-100 text-n-brand': sortKey === 'read' }"
                  />
                </div>
              </th>

              <!-- Motivo principal de rechazo -->
              <th class="px-3 py-3 min-w-[110px]">
                <div class="flex items-center gap-1">
                  <span>{{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.TABLE.REJECTION_REASON') }}</span>
                  <span
                    v-tooltip="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.TABLE.REJECTION_REASON_TOOLTIP')"
                    class="i-lucide-info text-[11px] text-n-slate-10 hover:text-n-slate-12 cursor-help"
                  />
                </div>
              </th>

              <!-- Última modificación -->
              <th
                class="px-3 py-3 cursor-pointer hover:text-n-slate-12 transition-colors whitespace-nowrap min-w-[130px]"
                @click="handleSort('last_updated_time')"
              >
                <div class="flex items-center gap-1.5">
                  <span>{{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.TABLE.LAST_MODIFIED') }}</span>
                  <span
                    class="i-lucide-arrow-down text-[11px]"
                    :class="{
                      'text-n-brand': sortKey === 'last_updated_time' && sortOrder === 'desc',
                      'rotate-180 text-n-brand': sortKey === 'last_updated_time' && sortOrder === 'asc',
                    }"
                  />
                </div>
              </th>

              <!-- Acciones -->
              <th class="px-3 py-3 text-right pr-4 min-w-[90px] whitespace-nowrap">
                <span>{{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.TABLE.ACTIONS') }}</span>
              </th>
            </tr>
          </thead>

          <tbody class="divide-y divide-n-weak">
            <tr
              v-for="template in sortedTemplates"
              :key="template.id || template.name"
              class="hover:bg-n-alpha-1 transition-colors group cursor-pointer"
              @click="$emit('view', template)"
            >
              <!-- Checkbox -->
              <td class="w-10 px-3 py-3 text-center" @click.stop>
                <input
                  type="checkbox"
                  :checked="selectedTemplateIds.includes(template.id || template.name)"
                  class="rounded border-n-slate-7 text-n-brand focus:ring-n-brand cursor-pointer"
                  @change="toggleSelect(template.id || template.name)"
                />
              </td>

              <!-- Nombre de la plantilla -->
              <td class="px-3 py-3">
                <span
                  class="font-semibold text-n-slate-12 group-hover:text-n-brand transition-colors block truncate max-w-[200px]"
                >
                  {{ template.name }}
                </span>
              </td>

              <!-- Categoría -->
              <td class="px-3 py-3 text-n-slate-12 font-medium whitespace-nowrap">
                {{ $t(`INBOX_MGMT.WHATSAPP_TEMPLATES.CATEGORY.${template.category?.toUpperCase() || 'UTILITY'}`) }}
              </td>

              <!-- Idioma + Snippet -->
              <td class="px-3 py-3 max-w-[220px]">
                <div class="flex flex-col">
                  <span class="text-n-slate-12 font-medium">
                    {{ formatLanguage(template.language) }}
                  </span>
                  <span
                    class="text-[11px] text-n-slate-10 truncate block max-w-[200px]"
                  >
                    {{ getBodySnippet(template) }}
                  </span>
                </div>
              </td>

              <!-- Estado Badge -->
              <td class="px-3 py-3 whitespace-nowrap">
                <span
                  class="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-full"
                  :class="[
                    getStatusBadge(template).bg,
                    getStatusBadge(template).text,
                  ]"
                >
                  <span
                    class="size-1.5 rounded-full"
                    :class="getStatusBadge(template).dot"
                  />
                  {{ getStatusBadge(template).label }}
                </span>
              </td>

              <!-- Mensajes enviados -->
              <td class="px-3 py-3 text-n-slate-12 whitespace-nowrap">
                <div
                  v-if="getTemplateAnalytics(template).sent > 0"
                  class="font-medium"
                >
                  {{ getTemplateAnalytics(template).sent.toLocaleString() }}
                </div>
                <div v-else class="flex flex-col">
                  <div class="flex items-center gap-1 text-n-slate-11">
                    <span>--</span>
                    <span
                      v-tooltip="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.TABLE.LESS_THAN_1000_TOOLTIP')"
                      class="i-lucide-info text-[10px] text-n-slate-10"
                    />
                  </div>
                  <span class="text-[10px] text-n-slate-10 truncate">
                    {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.TABLE.LESS_THAN_1000') }}
                  </span>
                </div>
              </td>

              <!-- Mensajes abiertos -->
              <td class="px-3 py-3 text-n-slate-12 font-medium whitespace-nowrap">
                {{ getTemplateAnalytics(template).read || 0 }}
              </td>

              <!-- Motivo principal de rechazo -->
              <td class="px-3 py-3 text-n-slate-11 whitespace-nowrap">
                <span
                  v-if="template.rejected_reason && template.rejected_reason !== 'NONE'"
                  class="text-n-ruby-10 font-medium truncate block max-w-[120px]"
                >
                  {{ template.rejected_reason }}
                </span>
                <span v-else>--</span>
              </td>

              <!-- Última modificación -->
              <td class="px-3 py-3 text-n-slate-11 whitespace-nowrap min-w-[130px]">
                {{ formatDate(template.last_updated_time || template.created_at) }}
              </td>

              <!-- Acciones -->
              <td class="px-3 py-3 text-right pr-4 whitespace-nowrap" @click.stop>
                <div class="flex items-center justify-end gap-1">
                  <!-- Edit -->
                  <NextButton
                    v-if="canEdit(template)"
                    v-tooltip="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.EDIT_TITLE')"
                    xs
                    ghost
                    slate
                    icon="i-lucide-pencil"
                    @click="$emit('edit', template)"
                  />

                  <!-- Delete -->
                  <NextButton
                    v-tooltip="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE_CONFIRM.TITLE')"
                    xs
                    ghost
                    slate
                    icon="i-lucide-trash-2"
                    @click="$emit('delete', template)"
                  />

                  <!-- Meta External Link -->
                  <a
                    v-tooltip="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.META_LINK.TOOLTIP')"
                    :href="metaTemplateUrl(template)"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex items-center justify-center size-6 rounded-md text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-blue-11 transition-colors"
                  >
                    <span class="i-lucide-external-link text-sm" />
                  </a>
                </div>
              </td>
            </tr>

            <tr v-if="sortedTemplates.length === 0">
              <td colspan="10" class="py-12 text-center text-n-slate-11">
                <div class="flex flex-col items-center justify-center gap-2">
                  <span class="i-lucide-file-search text-3xl text-n-slate-9" />
                  <p class="text-sm font-medium">
                    {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.EMPTY_STATE.TITLE') }}
                  </p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Table Footer Count Bar -->
      <div
        class="flex items-center justify-between px-4 py-2.5 bg-n-alpha-1 border-t border-n-weak text-xs text-n-slate-11 font-medium"
      >
        <div class="flex items-center gap-1.5">
          <span>
            {{
              $t('INBOX_MGMT.WHATSAPP_TEMPLATES.TABLE.FOOTER_COUNT', {
                showing: sortedTemplates.length,
                active: activeTemplatesCount,
                limit: 6000,
              })
            }}
          </span>
          <span
            v-tooltip="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.TABLE.TOTAL_LIMIT_TOOLTIP')"
            class="i-lucide-info text-xs text-n-slate-10 hover:text-n-slate-12 cursor-help"
          />
        </div>
      </div>
    </div>
  </div>
</template>
